import { useState, useRef, useLayoutEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/ui/spinner';
import { useCriteria } from '../hooks/useCriteria';
import { CriterionType, CRITERIA_VALIDATION, type Criterion } from '@promo/shared';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { EditableTextarea, type MarkdownMode } from '@/components/ui/editable-textarea';
import { EditableInput } from '@/components/ui/editable-input';
import { CharacterCount } from '@/components/ui/character-count';

// Form validation schema
const criterionSchema = z.object({
  criterionType: z.nativeEnum(CriterionType),
  description: z
    .string()
    .min(1, 'Description is required')
    .max(
      CRITERIA_VALIDATION.MAX_DESCRIPTION_LENGTH,
      `Description must be ${CRITERIA_VALIDATION.MAX_DESCRIPTION_LENGTH} characters or less`
    ),
  evidence: z
    .string()
    .min(1, 'Evidence is required')
    .max(
      CRITERIA_VALIDATION.MAX_EVIDENCE_LENGTH,
      `Evidence must be ${CRITERIA_VALIDATION.MAX_EVIDENCE_LENGTH} characters or less`
    ),
});

type CriterionForm = z.infer<typeof criterionSchema>;

interface CriterionFormProps {
  onSubmit: (data: CriterionForm) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  criterion?: Criterion;
  availableTypes: CriterionType[];
  isSubmitting?: boolean;
  isNew?: boolean;
  readOnly?: boolean;
}

function CriterionForm({
  onSubmit,
  onCancel,
  onDelete,
  criterion,
  availableTypes,
  isSubmitting = false,
  isNew = false,
  readOnly: formIsGloballyReadOnly = false,
}: CriterionFormProps) {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [focusTarget, setFocusTarget] = useState<
    'description' | 'evidence' | 'criterionType' | null
  >(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [evidenceMarkdownMode, setEvidenceMarkdownMode] = useState<MarkdownMode>('edit');
  const formRef = useRef<HTMLFormElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const evidenceRef = useRef<HTMLTextAreaElement>(null);
  const criterionTypeRef = useRef<HTMLElement | null>(null);

  const defaultFormValues = useMemo(() => {
    if (criterion) {
      return {
        criterionType: criterion.criterionType,
        description: criterion.description,
        evidence: criterion.evidence,
      };
    } else {
      return {
        criterionType: undefined,
        description: '',
        evidence: '',
      };
    }
  }, [criterion]);

  const form = useForm<CriterionForm>({
    resolver: zodResolver(criterionSchema),
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });

  useLayoutEffect(() => {
    form.reset(defaultFormValues);
  }, [defaultFormValues, form.reset, form]);

  useLayoutEffect(() => {
    if (formIsGloballyReadOnly) {
      setIsInEditMode(false);
      setFocusTarget(null);
    }
  }, [formIsGloballyReadOnly]);

  useLayoutEffect(() => {
    if (isInEditMode && !formIsGloballyReadOnly && focusTarget) {
      const timerId = setTimeout(() => {
        if (focusTarget === 'evidence' && evidenceRef.current) {
          evidenceRef.current.focus();
        } else if (focusTarget === 'criterionType' && criterionTypeRef.current) {
          criterionTypeRef.current.focus();
          if (typeof criterionTypeRef.current.click === 'function') {
            criterionTypeRef.current.click();
          }
        } else if (focusTarget === 'description' && descriptionRef.current) {
          descriptionRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [isInEditMode, focusTarget, formIsGloballyReadOnly]);

  const handleSubmit = (data: CriterionForm) => {
    onSubmit(data);
    if (!isNew) {
      setIsInEditMode(false);
      setFocusTarget(null);
    }
  };

  const handleActualCancel = () => {
    form.reset(defaultFormValues);
    setIsInEditMode(false);
    setFocusTarget(null);
    setShowCancelConfirm(false);
    if (onCancel) onCancel();
  };

  const handleCancelClick = () => {
    const currentValues = form.getValues();
    const hasChanges =
      currentValues.criterionType !== defaultFormValues.criterionType ||
      currentValues.description !== defaultFormValues.description ||
      currentValues.evidence !== defaultFormValues.evidence;

    if (hasChanges && isInEditMode) {
      setShowCancelConfirm(true);
    } else {
      handleActualCancel();
    }
  };

  const handleEnterEditMode = (target: 'description' | 'evidence' | 'criterionType') => {
    if (!isInEditMode && !formIsGloballyReadOnly) {
      setIsInEditMode(true);
      setFocusTarget(target);
      // Reset markdown mode to edit when entering edit mode
      if (target === 'evidence') {
        setEvidenceMarkdownMode('edit');
      }
    }
  };

  const typeOptions: SelectOption[] = availableTypes.map((value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
  }));

  const effectiveFieldReadOnly = formIsGloballyReadOnly || !isInEditMode;

  return (
    <div className={`py-6 space-y-4 border-b last:border-b-0`}>
      <form ref={formRef} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-4">
          <div>
            <FormField label="Description">
              <Controller
                name="description"
                control={form.control}
                render={({ field }) => {
                  const { ref: fieldRef, ...restField } = field;
                  return (
                    <EditableInput
                      ref={(el) => {
                        descriptionRef.current = el;
                        fieldRef(el);
                      }}
                      {...restField}
                      readOnly={effectiveFieldReadOnly}
                      disabled={isSubmitting}
                      placeholder="Describe the criterion the candidate is being evaluated on"
                      maxLength={CRITERIA_VALIDATION.MAX_DESCRIPTION_LENGTH}
                      onClick={
                        !isInEditMode && !formIsGloballyReadOnly
                          ? () => handleEnterEditMode('description')
                          : undefined
                      }
                    />
                  );
                }}
              />
              {isInEditMode && !formIsGloballyReadOnly && (
                <CharacterCount
                  current={String(form.watch('description') ?? '').length}
                  max={CRITERIA_VALIDATION.MAX_DESCRIPTION_LENGTH}
                />
              )}
              {form.formState.errors.description && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.description.message}
                </p>
              )}
            </FormField>
          </div>

          <div>
            <FormField label="Type">
              <div className="relative">
                {!isInEditMode && !formIsGloballyReadOnly && (
                  <div
                    onClick={() => handleEnterEditMode('criterionType')}
                    className="absolute inset-0 z-10 cursor-pointer"
                    aria-label="Edit type"
                  />
                )}
                <Controller
                  name="criterionType"
                  control={form.control}
                  render={({ field }) => {
                    const { ref: rhfRef, value, onChange, ...restFieldProps } = field;

                    // Define setRefs directly in the render prop without useCallback
                    const setRefs = (node: HTMLElement | null) => {
                      // Call react-hook-form's ref
                      if (typeof rhfRef === 'function') {
                        rhfRef(node);
                      } else if (rhfRef) {
                        (rhfRef as React.MutableRefObject<HTMLElement | null>).current = node;
                      }
                      // Assign to your own ref for focusing
                      criterionTypeRef.current = node;
                    };

                    return (
                      <EditableSelect
                        {...restFieldProps}
                        value={value}
                        onValueChange={onChange}
                        ref={setRefs}
                        options={typeOptions}
                        disabled={isSubmitting || effectiveFieldReadOnly}
                        placeholder="Pick a type"
                      />
                    );
                  }}
                />
              </div>
              {form.formState.errors.criterionType && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.criterionType.message}
                </p>
              )}
            </FormField>
          </div>

          <div>
            <FormField label="Evidence">
              <Controller
                name="evidence"
                control={form.control}
                render={({ field }) => {
                  const { ref: fieldRef, ...restField } = field;
                  return (
                    <EditableTextarea
                      ref={(el) => {
                        evidenceRef.current = el;
                        fieldRef(el);
                      }}
                      {...restField}
                      markdown={true}
                      showModeToggle={isInEditMode && !formIsGloballyReadOnly}
                      markdownMode={evidenceMarkdownMode}
                      onModeChange={setEvidenceMarkdownMode}
                      readOnly={effectiveFieldReadOnly}
                      disabled={isSubmitting}
                      placeholder="Cite detailed examples of how the candidate met the criterion"
                      maxLength={CRITERIA_VALIDATION.MAX_EVIDENCE_LENGTH}
                      rows={4}
                      onClick={
                        !isInEditMode && !formIsGloballyReadOnly
                          ? () => handleEnterEditMode('evidence')
                          : undefined
                      }
                    />
                  );
                }}
              />
              {isInEditMode && !formIsGloballyReadOnly && (
                <CharacterCount
                  current={String(form.watch('evidence') ?? '').length}
                  max={CRITERIA_VALIDATION.MAX_EVIDENCE_LENGTH}
                />
              )}
              {form.formState.errors.evidence && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.evidence.message}
                </p>
              )}
            </FormField>
          </div>
        </div>

        {isInEditMode && !formIsGloballyReadOnly && (
          <div className="flex justify-end gap-2 mt-4">
            {onDelete && !isNew && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={handleCancelClick}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button type="submit" disabled={isSubmitting || (!form.formState.isDirty && !isNew)}>
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        )}
      </form>

      {onDelete && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Criterion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this criterion? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={isSubmitting}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (onDelete) onDelete();
                  setShowDeleteConfirm(false);
                  setIsInEditMode(false);
                  setFocusTarget(null);
                }}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {showCancelConfirm && (
        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes in this criterion. Discard them?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={isSubmitting}
                onClick={() => setShowCancelConfirm(false)}
              >
                Continue Editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleActualCancel}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

interface CriteriaSectionProps {
  packetId: string;
  isEditor: boolean;
  isCandidate: boolean;
  readOnly?: boolean;
}

export function CriteriaSection({ packetId, isEditor, isCandidate, readOnly = false }: CriteriaSectionProps) {
  const {
    criteria,
    isLoadingCriteria,
    criteriaError,
    createCriterion,
    updateCriterion,
    deleteCriterion,
    isCreatingCriterion,
    isUpdatingCriterion,
    isDeletingCriterion,
    getCriteriaCountByType,
  } = useCriteria(packetId);

  // State for forcing remount of the new form
  const [newFormKey, setNewFormKey] = useState(0);

  const canEdit = !readOnly && (isEditor || isCandidate);

  // Determine if adding new criteria is allowed (per-type and total limits)
  const canAddNew =
    Object.values(CriterionType).length > 0 &&
    criteria.length < CRITERIA_VALIDATION.MAX_CRITERIA_TOTAL;

  // Group criteria by type
  const criteriaByType = Object.values(CriterionType).map((type) => {
    return {
      type,
      criteria: criteria.filter((c) => c.criterionType === type),
      count: getCriteriaCountByType(type),
    };
  });

  // Memoized available types for the new criterion form
  const memoizedAvailableTypesForNewForm = useMemo(() => {
    return Object.values(CriterionType).filter(
      (type) => getCriteriaCountByType(type) < CRITERIA_VALIDATION.MAX_CRITERIA_PER_TYPE
    );
  }, [getCriteriaCountByType]);

  // Stable list of all criterion types for existing forms
  const allCriterionTypes = useMemo(() => Object.values(CriterionType), []);

  const handleCreateCriterion = (data: CriterionForm) => {
    createCriterion(
      {
        ...data,
        packetId: packetId,
      },
      {
        onSuccess: () => setNewFormKey((k) => k + 1),
      }
    );
  };

  const handleUpdateCriterion = (id: string, data: CriterionForm) => {
    updateCriterion(id, data);
  };

  if (isLoadingCriteria) {
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Criteria</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading criteria...</span>
        </div>
      </div>
    );
  }

  if (criteriaError) {
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Criteria</h2>
        </div>
        <div className="bg-red-50 text-red-800 p-4 rounded-md">
          <h3 className="text-lg font-semibold">Error loading criteria</h3>
          <p>{criteriaError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="flex justify-between lg:justify-start gap-2 items-center mb-6">
        <h2 className="text-xl font-semibold">Criteria</h2>
        <Badge variant="default">
          {criteria.length} of {CRITERIA_VALIDATION.MAX_CRITERIA_TOTAL}
        </Badge>
      </div>
      <div>
        <p className="mb-4">
          Highlight the criteria on which the candidate is being evaluated for the Target Title
          above. Select relevant criteria for the Target Title from the{' '}
          <a
            className="text-blue-500"
            href="https://docs.google.com/presentation/d/170AS5zm__lzSbRptZPIK49rBRPz2S5mRrYs1AXGx6Vk/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Career Ladder
          </a>
          . For each criterion, provide detailed examples of how the candidate met the criterion.
        </p>
        <p className="mb-4">
          For each criterion, choose the most relevant type from the dropdown. Aim to have at least
          one criterion for each type, up to 3 per type. This encourages a well-rounded evaluation.
        </p>
      </div>

      <div>
        {criteriaByType.map(
          ({ type, criteria: typeCriteria }) =>
            typeCriteria.length > 0 && (
              <div key={type} className="mb-6">
                <div className="flex justify-between lg:justify-start gap-2 items-center">
                  <h3 className="text-sm text-gray-600 font-bold">{type}</h3>
                  <Badge variant="secondary">
                    {typeCriteria.length} of {CRITERIA_VALIDATION.MAX_CRITERIA_PER_TYPE}
                  </Badge>
                </div>
                <div>
                  {typeCriteria.map((criterion) => (
                    <CriterionForm
                      key={criterion.id}
                      criterion={criterion}
                      onSubmit={(data) => handleUpdateCriterion(criterion.id, data)}
                      onDelete={canEdit ? () => deleteCriterion(criterion.id) : undefined}
                      isSubmitting={isUpdatingCriterion || isDeletingCriterion}
                      availableTypes={allCriterionTypes}
                      readOnly={!canEdit || isUpdatingCriterion || isDeletingCriterion}
                      isNew={false}
                    />
                  ))}
                </div>
              </div>
            )
        )}
      </div>

      {canEdit && canAddNew && (
        <div>
          <CriterionForm
            key={newFormKey}
            onSubmit={handleCreateCriterion}
            isSubmitting={isCreatingCriterion}
            availableTypes={memoizedAvailableTypesForNewForm}
            isNew={true}
            readOnly={!canEdit}
          />
        </div>
      )}
    </div>
  );
}
